import { Component } from "react";

import { ErrorPageContainer } from "components/ErrorPage/ErrorPageContainer";
import { GlobalErrorDialog } from "components/GlobalErrorDialog/GlobalErrorDialog";
import { trackException } from "services/applicationInsights";

import IPAddressError from "./IPAddressError/IPAddressError";

import type { History } from "history";
import type { ErrorDetails } from "models";
import type { State } from "reducers";

// Now we do not have the ability to recover from errors
// So I am using only local error from the state

interface ErrorBoundaryProps {
  globalState: State;
  history: History;
  forcedError?: ErrorDetails;
  ipForbiddenError?: boolean | null;
}

interface ErrorBoundaryState {
  error?: ErrorDetails;
  showErrorDialog: boolean;
  globalError?: ErrorDetails;
  stackCopied?: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      error: undefined,
      showErrorDialog: false,
      stackCopied: false,
    };
  }

  componentDidCatch(
    error: { message: string; name: string },
    errorInfo: { componentStack: string },
  ) {
    // Display fallback UI
    this.setState({
      error: {
        error: error.message,
        name: error.name,
        stack: errorInfo.componentStack,
      },
    });

    // Track error with Application Insights
    trackException(new Error(error.message), {
      componentStack: errorInfo.componentStack,
      errorBoundary: "ErrorBoundary",
      errorName: error.name,
      url: window.location.href,
    });
  }

  componentDidMount() {
    window.onerror = (message, source, lineno, colno, error) => {
      this.showGlobalError(message, error?.stack);

      // Track global JavaScript errors
      trackException(error || new Error(String(message)), {
        source,
        lineno,
        colno,
        errorType: "GlobalJavaScriptError",
        url: window.location.href,
      });

      return true;
    };

    // Track unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      trackException(new Error(`Unhandled Promise Rejection: ${event.reason}`), {
        reason: String(event.reason),
        errorType: "UnhandledPromiseRejection",
        url: window.location.href,
      });
    });
  }

  render() {
    if (this.props.ipForbiddenError) {
      return <IPAddressError />;
    }

    if (this.state.error?.error || this.state.globalError || this.props.forcedError) {
      let content;

      // You can render any custom fallback UI
      if (this.state.error?.error) {
        content = (
          <ErrorPageContainer customError={this.state.error} history={this.props.history} />
        );
      } else if (this.props.forcedError) {
        content = (
          <ErrorPageContainer customError={this.props.forcedError} history={this.props.history} />
        );
      }

      return (
        <div>
          {content}
          {this.state.globalError && process.env.REACT_APP_QA_MODE === "yes" && (
            <GlobalErrorDialog
              errorDetails={this.state.globalError}
              visible={this.state.showErrorDialog}
              onCopyClick={this.handleOnCopyError}
              toggleDialog={this.handleDismissError}
              stackCopied={this.state.stackCopied}
              globalState={this.props.globalState}
            />
          )}
          {this.state.globalError && process.env.REACT_APP_QA_MODE !== "yes" && (
            <ErrorPageContainer customError={this.state.globalError} history={this.props.history} />
          )}
        </div>
      );
    }

    return this.props.children;
  }

  handleOnCopyError = () => {
    const dummyElement = document.createElement("textarea");
    document.body.appendChild(dummyElement);
    dummyElement.value = `${this.state.globalError.stack}\n\n State Data: \n ${JSON.stringify(this.props.globalState, null, 2)}`;
    dummyElement.select();
    document.execCommand("copy");
    document.body.removeChild(dummyElement);
    this.setState({ stackCopied: true });

    setTimeout(() => {
      this.setState({ stackCopied: false });
    }, 3000);
  };

  handleDismissError = () => {
    this.setState({
      showErrorDialog: false,
    });
  };

  showGlobalError = (message: any, error: any) => {
    this.setState({
      globalError: {
        error: message,
        name: message,
        stack: error,
      },
      showErrorDialog: true,
    });
  };
}
